import difference from 'lodash/difference';
import round from 'lodash/round';
import zip from 'lodash/zip';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {
  ChartType as CommonChartType,
  ChartVizType,
  PropertyType,
  REPORT_LIMITS,
  ReportName,
  ResourceType,
  SPECIAL_EVENTS,
} from 'common/report/constants';
import {
  FunnelQueryResponse,
  isSegmentedData,
  KeyedByDate,
  SankeyData,
  SegmentedFunnelResults,
  UnsegmentedFunnelResult,
} from 'common/report/funnels/models/api-types';
import {
  FunnelOrder,
  OTHER_PROP,
  OVERALL_PROP,
  OVERALL_PROP_NAME,
  SEGMENT_SEPARATOR,
} from 'common/report/funnels/models/constants';
import {
  BasicFunnelStep,
  ChartType,
  ColorChoice,
  DateKeyedFunnelSegments,
  FunnelBookmark,
  FunnelData,
  FunnelsAggregateType,
  FunnelSegment,
  FunnelStep,
  isOrStep,
  SegmentId,
  SegmentPropToDetailsMap,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {propertiesUtils, renamePropertyOld, renamePropertyValue} from 'common/report/util/properties';
import {
  getFirstSegment,
  getSegmentPropertyType,
  isCohortSegment,
  isEventSegment,
  isUserSegment,
  OtherSegmentation,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {CommonDataTableFields, SegmentColumnSpec} from 'common/components/data-table/types';
import {getChartSegmentColorCombo} from 'common/util/colors';
import {SelectMode} from 'common/charts/contexts/segment-filter-controller';
import {getDefaultSortConfig} from 'common/report/util/sorting/table-sorting';
import {TrackName as DataTableTrackName} from 'common/components/data-table/types';
import {SortBy, SortByValueConfig, SortConfig, SortOrder} from 'common/report/util/sorting/types';
import {pluralize} from 'common/util/string';
import {
  convertSegfilterToPropertyFilter,
  isSegmentationFilter,
} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {FUNNELS_TIME_TO_CONVERSION} from 'common/query-builder/query-block/properties-menu/constants';
import {useReportConsistency, useSingleReport} from 'common/report/util/feature-sampling';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';

import type {FunnelsQuery} from '../models/funnels-query';
import {maybeAssignColors} from './colors';

export const UNIT_IN_SECONDS = {
  month: 30 * 24 * 60 * 60,
  week: 7 * 24 * 60 * 60,
  day: 24 * 60 * 60,
  hour: 60 * 60,
  minute: 60,
  second: 1,
};

const BUCKET_SIZES = [
  1,
  2,
  3,
  4,
  5,
  10,
  20,
  30,
  1 * UNIT_IN_SECONDS.minute,
  2 * UNIT_IN_SECONDS.minute,
  3 * UNIT_IN_SECONDS.minute,
  4 * UNIT_IN_SECONDS.minute,
  5 * UNIT_IN_SECONDS.minute,
  10 * UNIT_IN_SECONDS.minute,
  20 * UNIT_IN_SECONDS.minute,
  30 * UNIT_IN_SECONDS.minute,
  1 * UNIT_IN_SECONDS.hour,
  2 * UNIT_IN_SECONDS.hour,
  3 * UNIT_IN_SECONDS.hour,
  4 * UNIT_IN_SECONDS.hour,
  5 * UNIT_IN_SECONDS.hour,
  6 * UNIT_IN_SECONDS.hour,
  12 * UNIT_IN_SECONDS.hour,
  1 * UNIT_IN_SECONDS.day,
  2 * UNIT_IN_SECONDS.day,
  3 * UNIT_IN_SECONDS.day,
  4 * UNIT_IN_SECONDS.day,
  5 * UNIT_IN_SECONDS.day,
  10 * UNIT_IN_SECONDS.day,
  20 * UNIT_IN_SECONDS.day,
  30 * UNIT_IN_SECONDS.day,
];

const LOWER_NUM_BUCKETS = 12;
const UPPER_NUM_BUCKETS = 24;
const NUM_BUCKET_LIMIT = 90;

/**
 * Given a date string, returns a date string in full date format.
 * Full date formats prevent the browser from imposing its own timezone assumptions when using the Date object.
 */
export function formatDateString(date: string): string {
  if (moment(date, `YYYY-MM-DD`, true).isValid()) {
    date = date.concat(`T00:00:00`);
  }
  return date;
}

export function formatPercent(decimal, precision = 1, appendPct = true, fixedPoint = false) {
  let pct: number | string = Math.round(decimal * Math.pow(10, precision + 2)) / Math.pow(10, precision);
  if (fixedPoint) {
    pct = pct.toFixed(precision);
  }
  return appendPct ? pct + `%` : pct;
}

/** Creates the 'Overall' FunnelSegment using the Overall prop and propName */
export function generateOverallSegment(steps: FunnelData = []): FunnelSegment {
  steps = steps.map(addIndex);
  return {
    prop: OVERALL_PROP,
    propName: OVERALL_PROP_NAME,
    props: [{prop: OVERALL_PROP, propName: OVERALL_PROP_NAME}],
    steps,
  };
}

/** Flatten nested segment props into single array of segments. */
export function collectNestedSegments(nestedSegments: SegmentedFunnelResults, props: string[] = []): FunnelSegment[] {
  const segmentsResult = Object.entries(nestedSegments).map(([key, value]) => {
    if (isFunnelStepArray(value)) {
      const segmentProps = props.concat(key).map((prop) => ({prop}));
      const segmentProp = segmentProps.map((seg) => seg.prop).join(SEGMENT_SEPARATOR);
      const segmentPropName = getCombinedPropsAsDisplayName(segmentProps, segmentProp);
      return {
        props: segmentProps,
        prop: segmentProp,
        propName: segmentPropName,
        steps: value.map(addIndex),
      };
    } else {
      return collectNestedSegments(value, props.concat(key));
    }
  });

  return segmentsResult.flat();
}

function isFunnelStepArray(d: SegmentedFunnelResults['segment']): d is Array<BasicFunnelStep | FunnelStep> {
  return Array.isArray(d);
}

/** Combine individual segment props into one combined propName to be displayed */
function getCombinedPropsAsDisplayName(segmentProps: SegmentId[], segmentProp: string): string {
  let segmentPropName = ` `;
  if (segmentProp === OVERALL_PROP) {
    segmentPropName = OVERALL_PROP_NAME;
  } else {
    // TODO: Once segment renaming for funnels is added to common,
    // propName should be the result of concatenated, RENAMED props joined by the SEGMENT_SEPERATOR
    const concatProps = segmentProps.map((seg) => seg.prop).join(SEGMENT_SEPARATOR);
    segmentPropName = concatProps !== `` ? concatProps : segmentPropName;
  }
  return segmentPropName;
}

function addIndex<T>(item: T, index: number): T & {index: number} {
  return {...item, index};
}

/**
 * Given an array of segments and a number of segments to select, returns an array containing the prop values of the
 * first segments in the given array up to the number of segments to select.
 *
 * @param topSegments array of funnel segments, the first segments will be the ones whose prop values are returned
 * @param numSelections the number of segment prop values to return from the beginning of the given array
 * @return {string[]} array of the prop values from the beginning of the segment array up to the numSelections provided
 */
export function getDefaultSelectedSegments(topSegments: FunnelSegment[], numSelections: number): string[] {
  const segmentProps = topSegments.map((segment) => segment.prop);
  return segmentProps.slice(0, numSelections);
}

export function getSelectedSegments(
  selected: ColorChoice[] = [],
  selectMode: SelectMode = SelectMode.Position,
  topSegments: FunnelSegment[],
  commonChartType: CommonChartType,
): ColorChoice[] {
  let selectedKeys = selected.map((choice) => choice.key);
  // if the selected segments are no longer in the top choices, invalidate the segments
  const propSet = new Set(topSegments.map((seg) => seg.prop));
  const invalidateSelected = !selectedKeys.every((key) => propSet.has(key));

  // Positional means take either top DEFAULT_SEGMENTS or top MAX_SEGMENTS
  if (invalidateSelected || selectMode === SelectMode.Position) {
    selectedKeys = getDefaultSelectedSegments(
      topSegments,
      !invalidateSelected &&
        selectedKeys.length ===
          Math.min(REPORT_LIMITS[ReportName.Funnels].segments[commonChartType].max, topSegments.length)
        ? REPORT_LIMITS[ReportName.Funnels].segments[commonChartType].max
        : REPORT_LIMITS[ReportName.Funnels].segments[commonChartType].default,
    );
  }

  return maybeAssignColors(selected, selectedKeys);
}

/**
 * Given an array of date keyed segments and a number of segments to select, returns an array containing the prop values of the
 * first segments of the first date in the given array up to the number of segments to select.
 *
 * @param topSegments array of date keyed funnel segments, the first segments will be the ones whose prop values are returned
 * @param numSelections the number of segment prop values to return from the beginning of the given array
 * @return {string[]} array of the prop values from the beginning of the segment array up to the numSelections provided
 */
export function getDefaultSelectedTrends(topTrends: DateKeyedFunnelSegments[], numSelections: number): string[] {
  let defaultSelected = [];
  if (topTrends.length > 0) {
    defaultSelected = getDefaultSelectedSegments(topTrends[0].segments, numSelections);
  }
  return defaultSelected;
}

/**
 * Given a conversion window, returns an appropriate bucket size in seconds
 * such that LOWER_NUM_BUCKETS < #buckets < UPPER_NUM_BUCKETS
 * @param {number} conversionWindow - conversion window length in seconds
 * @returns {number} bucket size in seconds, default 1 second
 */
export function getBucketSize(conversionWindow) {
  return BUCKET_SIZES.reduce((selectedBucket, bucketSize) => {
    const numBuckets = Math.ceil(conversionWindow / (1.0 * bucketSize));
    return LOWER_NUM_BUCKETS <= numBuckets && numBuckets <= UPPER_NUM_BUCKETS ? bucketSize : selectedBucket;
  }, 1);
}

export function getAllowedTrendUnits(numDays: number): TrendUnit[] {
  const allowedTrendUnits = [];
  // numDays = 1 allows the query to work between one day and the next which is really 2 days
  // To limit minute to a 24 hour window, numDay should be 0
  if (numDays <= 0) {
    allowedTrendUnits.push(TrendUnit.Minute);
  }
  // In order to have about the NUM_BUCKET_LIMIT for hours, we found NUM_BUCKET_LIMIT/24 and then rounded up to the closest unit which is about a week (7 days)
  if (numDays <= 7) {
    allowedTrendUnits.push(TrendUnit.Hour);
  }
  if (numDays <= NUM_BUCKET_LIMIT) {
    allowedTrendUnits.push(TrendUnit.Day);
  }
  if (numDays <= NUM_BUCKET_LIMIT * 7) {
    allowedTrendUnits.push(TrendUnit.Week);
  }
  allowedTrendUnits.push(TrendUnit.Month);
  allowedTrendUnits.push(TrendUnit.Quarter);
  return allowedTrendUnits;
}

export function getQbOtherFilterOptions(): Array<PropertySectionItem<string>> {
  return useSingleReport() || useReportConsistency()
    ? [FUNNELS_TIME_TO_CONVERSION, {value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
    : [];
}

export function getQbOtherGroupOptions(funnelsQuery: FunnelsQuery): Array<PropertySectionItem<string>> {
  //TODO - for Multimetric stuff, funnelsQuery can be NULL for now.  Eventually, it should never be null.
  return [
    ...(useSingleReport() || useReportConsistency() ? [FUNNELS_TIME_TO_CONVERSION] : []),
    ...(useSingleReport() || useReportConsistency()
      ? [{value: SPECIAL_EVENTS.SELECTED_STEP} as PropertySectionItem<string>]
      : []),
    ...(funnelsQuery?.isAnyOrderFunnel() ? [{value: `$paths`} as PropertySectionItem<string>] : []),
  ];
}

/**
 * convert a raw string to an enum value if it exists, else return a default
 * @param value raw string value to convert
 * @param enumMap enum type
 * @param defaultMember default instance of enum
 */
export function fromEnum<T>(value: string, enumType: T, defaultMember: T[keyof T]): T[keyof T] {
  if (Object.values(enumType).includes(value)) {
    return (value as unknown) as T[keyof T];
  } else {
    return defaultMember;
  }
}

/**
 * Returns an appropriate list of time buckets for the given conversion window.
 * Optimizes for showing between 12-24 evenly spaced buckets.
 * @param window the conversion window for the funnel
 */
export function getTimeBucketsFromConversionWindow(window) {
  // for sessions, approximate the conversion window length as NUM_SESSIONS * NUM_SECONDS_PER_DAY
  // (since each session is at most a day long)
  const windowUnit = window.unit === `session` ? `day` : window.unit;
  const windowSecs = window.value * UNIT_IN_SECONDS[windowUnit];

  return {
    min: 0,
    max: windowSecs,
    offset: 0,
    bucketSize: getBucketSize(windowSecs),
    isDrilledDown: false,
  };
}

export function getOverallStepsFromDatedSegments(
  segmentData: DateKeyedFunnelSegments[],
): Array<BasicFunnelStep | FunnelStep> {
  if (!segmentData || segmentData.length === 0) {
    return [];
  }
  const {segments} = segmentData[0];
  return getOverallStepsFromFunnelSegment(segments);
}

export function getOverallStepsFromFunnelSegment(segments: FunnelSegment[]): Array<BasicFunnelStep | FunnelStep> {
  return segments.find((s) => s.prop === OVERALL_PROP)?.steps ?? [];
}

export function getOverallConversionRateFromSegments(segmentData: DateKeyedFunnelSegments[]): number {
  return getOverallStepsFromDatedSegments(segmentData).slice(-1)[0]?.overall_conv_ratio;
}

export function getHighestConversionRateFromSegments(segmentData: DateKeyedFunnelSegments[]): number {
  const {segments} = segmentData[0];
  return Math.max(
    ...segments.map((item) =>
      item.steps[0].count > 0 ? item.steps[item.steps.length - 1].count / item.steps[0].count : 0,
    ),
  );
}

export function isSegmented(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.[0]?.segments.length > 1;
}

export function hasData(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.some(({segments}) =>
    segments?.some((segment) => segment.steps.length && segment.steps[0].count > 0),
  );
}

export function hasSankeyData(sankeyData: SankeyData) {
  return sankeyData?.breakdowns?.length > 0 && sankeyData?.steps?.length > 0;
}

export function getCountTypeText({funnelsCountType, dataGroupId}) {
  if (funnelsCountType === `unique`) {
    return dataGroupId ? `uniques` : `unique users`;
  } else {
    return `total times`;
  }
}

export function convertSegmentLabelForCohort(
  label: string,
  segmentNames: Array<string | string[]>,
  transform: (data: {title: string; value: string}) => string = ({title, value}) => `${title}: ${value}`,
): string {
  return label
    .split(SEGMENT_SEPARATOR)
    .map((segmentValue, idx) => ({
      value: segmentValue,
      title: renamePropertyOld(segmentNames[idx]),
    }))
    .map(transform)
    .join(SEGMENT_SEPARATOR);
}

export function computeSelectionsOnSortChange({
  prevSelected,
  prevSelectMode,
  orderedRowIds,
  maxNumSelections,
  defaultNumSelections,
}: {
  prevSelected: string[];
  prevSelectMode: SelectMode;
  orderedRowIds: string[];
  maxNumSelections: number;
  defaultNumSelections: number;
}): {selected: string[]; selectMode: SelectMode} {
  let selected: string[];
  let selectMode: SelectMode;

  const rowsIncludeAllSelected = difference(prevSelected, orderedRowIds).length === 0;
  const selectedSegmentsPresent = prevSelectMode === SelectMode.Item && rowsIncludeAllSelected;
  const setToDefault = !prevSelectMode || prevSelectMode === SelectMode.Position || !selectedSegmentsPresent;
  if (setToDefault) {
    const useSelectedLength = prevSelectMode === SelectMode.Position && prevSelected.length === maxNumSelections;
    const limit = useSelectedLength ? prevSelected.length : defaultNumSelections;
    selected = orderedRowIds.slice(0, limit);
    selectMode = SelectMode.Position;
  } else {
    selected = [...prevSelected];
    selectMode = SelectMode.Item;
  }
  return {selected, selectMode};
}

export function computeSelectionsOnSelectionChange({
  prevSelected,
  orderedRowIds,
  isToggleAll,
  changed,
  maxNumSelections,
}: {
  prevSelected: string[];
  orderedRowIds: string[];
  isToggleAll: boolean;
  changed: {[id: string]: boolean};
  maxNumSelections: number;
}): {selected: string[]; selectMode: SelectMode} {
  let selected: string[];
  let selectMode: SelectMode;

  if (isToggleAll) {
    if (prevSelected.length) {
      selected = [];
      selectMode = SelectMode.Item;
    } else {
      selected = orderedRowIds.slice(0, maxNumSelections);
      selectMode = SelectMode.Position;
    }
  } else {
    selectMode = SelectMode.Item;
    const changeSet = new Set(prevSelected);
    for (const [id, isSelected] of Object.entries(changed)) {
      if (isSelected) {
        changeSet.add(id);
      } else {
        changeSet.delete(id);
      }
    }
    // TODO: this, but more efficiently.
    // Need to keep the order of selected segments the same as in the table.
    selected = Array.from(changeSet).sort((a, b) => {
      return orderedRowIds.findIndex((id) => id === a) - orderedRowIds.findIndex((id) => id === b);
    });
  }

  return {selected, selectMode};
}

/**
 * Returns true if the chart in the current appState uses the selected attr to save selected segments.
 * Returns false if the chart in the current appState uses the new savedLegend attr to save selected segments.
 */
export function usesFlatSelectedSegments(bookmarkParams: FunnelBookmark): boolean {
  return !(
    (bookmarkParams.chart_type === ChartType.Frequency && bookmarkParams.frequencyVizType === ChartVizType.Bar) ||
    (bookmarkParams.chart_type === ChartType.TimeToConvert && bookmarkParams.ttcVizType === ChartVizType.Bar)
  );
}

export function getDefaultFunnelTTCMetricSortConfig(bookmarkParams: FunnelBookmark) {
  const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams).length || 1;
  return getDefaultSortConfig({
    numSegmentColumns,
    valueSortField: CommonDataTableFields.Total,
  });
}

export function getDefaultFunnelTrendSortConfig(bookmarkParams: FunnelBookmark) {
  const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams).length || 1;
  return getDefaultSortConfig({
    numSegmentColumns,
    valueSortField: CommonDataTableFields.CohortSize,
    allowGroupSort: true,
  });
}

export const DEFAULT_STEP_SORT_FIELD = `step-0`;
export function getDefaultFunnelStepsSortConfig(numSegmentColumns: number) {
  return getDefaultSortConfig({
    numSegmentColumns,
    valueSortField: DEFAULT_STEP_SORT_FIELD,
    allowGroupSort: true,
  });
}

/** Converts from the old sorting config format or gets the default for funnel steps */
export function convertOrDefaultFunnelStepsSortConfig(bookmarkParams: FunnelBookmark) {
  const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams).length || 1;
  let sorting: SortConfig;
  if (bookmarkParams.sortField) {
    // convert the old sortField + sortOrder params to sorting
    const flatSortConfig: SortByValueConfig = {
      sortBy: SortBy.Value,
      sortOrder: bookmarkParams.sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending,
      valueField: bookmarkParams.sortField,
      colSortAttrs: [],
    };

    // this just populates `sorting.colSortAttrs`
    sorting = getDefaultSortConfig({
      numSegmentColumns,
      valueSortField: bookmarkParams.sortField,
      flatSortConfig,
    });
  } else {
    sorting = getDefaultFunnelStepsSortConfig(numSegmentColumns);
  }
  return sorting;
}

export const enum WidgetType {
  StepsMetricChart = `StepsMetricChart`,
  StepChart = `StepChart`,
  TrendsChart = `TrendsChart`,
  TTCLineChart = `TTCLineChart`,
  TTCMetricChart = `TTCMetricChart`,
  TTCAggregateChart = `TTCAggregateChart`,
  TTCDistributionChart = `TTCDistributionChart`,
  FrequencyLineChart = `FrequencyLineChart`,
  FrequencyAggregateChart = `FrequencyAggregateChart`,
  FrequencyDistributionChart = `FrequencyDistributionChart`,
  TopPathsChart = `TopPathsChart`,
}

export function getChartWidgetType({
  chart_type: chartType,
  stepsVizType,
  frequencyAggregateType,
  frequencyVizType,
  ttcAggregateType,
  ttcVizType,
}: FunnelBookmark) {
  switch (chartType) {
    case ChartType.Chart:
      if (stepsVizType === ChartVizType.Metric) {
        return WidgetType.StepsMetricChart;
      } else {
        return WidgetType.StepChart;
      }
    case ChartType.Trends:
      return WidgetType.TrendsChart;
    case ChartType.TimeToConvert:
      if (ttcVizType === ChartVizType.Line) {
        return WidgetType.TTCLineChart;
      } else if (ttcVizType === ChartVizType.Metric) {
        return WidgetType.TTCMetricChart;
      } else {
        // Bar Charts
        if (ttcAggregateType === FunnelsAggregateType.Median) {
          return WidgetType.TTCAggregateChart;
        } else {
          return WidgetType.TTCDistributionChart;
        }
      }
    case ChartType.Frequency:
      if (frequencyVizType === ChartVizType.Line) {
        return WidgetType.FrequencyLineChart;
      } else {
        // Bar Charts
        if (frequencyAggregateType === FunnelsAggregateType.Median) {
          return WidgetType.FrequencyAggregateChart;
        } else {
          return WidgetType.FrequencyDistributionChart;
        }
      }
    case ChartType.Sankey:
      return WidgetType.TopPathsChart;
  }

  return null;
}

export function getDefaultSortConfigForBookmark(bookmarkParams: FunnelBookmark): SortConfig {
  const widgetType = getChartWidgetType(bookmarkParams);

  if (widgetType === WidgetType.StepChart || widgetType === WidgetType.StepsMetricChart) {
    const numSegmentColumns = getSegmentColumnSpecs(bookmarkParams).length || 1;
    return getDefaultFunnelStepsSortConfig(numSegmentColumns);
  } else if (widgetType === WidgetType.TrendsChart) {
    return getDefaultFunnelTrendSortConfig(bookmarkParams);
  } else if (widgetType === WidgetType.TTCMetricChart) {
    return getDefaultFunnelTTCMetricSortConfig(bookmarkParams);
  } else {
    return undefined;
  }
}

export function getSortParamsIfWidgetTypeChange({
  newBookmarkParams,
  oldBookmarkParams,
}: {
  newBookmarkParams: FunnelBookmark;
  oldBookmarkParams: FunnelBookmark;
}): {sorting?: SortConfig} {
  const oldWidgetType = getChartWidgetType(oldBookmarkParams);
  const newWidgetType = getChartWidgetType(newBookmarkParams);
  if (newWidgetType !== oldWidgetType) {
    if (
      (oldWidgetType === WidgetType.StepChart && newWidgetType === WidgetType.StepsMetricChart) ||
      (oldWidgetType === WidgetType.StepsMetricChart && newWidgetType === WidgetType.StepChart)
    ) {
      // If switching between step bar chart <-> step metric chart, we can keep prior sorting because the data table
      // is exactly the same between these two visualizations.
      return {};
    } else {
      return {sorting: getDefaultSortConfigForBookmark(newBookmarkParams)};
    }
  }
  return {};
}

export const DEPRECATED_SEGMENTS = [OTHER_PROP, OVERALL_PROP];

export function sanitizeBookmarkParams(bookmarkParams: FunnelBookmark): Partial<FunnelBookmark> {
  function isDeprecatedSegment(selectedSegment: string) {
    return DEPRECATED_SEGMENTS.some((deprecatedSegment) => {
      // When using comparison events, the entire segment will be something like "1A Viewed Report / $overall". So
      // it's needed to do substring match for the special segment
      return selectedSegment.endsWith(deprecatedSegment);
    });
  }

  let updatedParams: Partial<FunnelBookmark> = {};

  if (bookmarkParams.selectMode) {
    updatedParams = {...updatedParams, selectMode: undefined};
  }

  if (isEmpty(bookmarkParams.sorting)) {
    if (bookmarkParams.chart_type === ChartType.Trends) {
      // For old Funnels Trends (which don't have new `sorting` param), convert them to have a default SortConfig.
      updatedParams = {
        ...updatedParams,
        // Old Funnels Trends store sortField/sortOrder even though they didn't apply. Clear them out to clean up the
        // persisted state.
        sortField: undefined,
        sortOrder: undefined,
        sorting: getDefaultFunnelTrendSortConfig(bookmarkParams),
      };
    } else if (bookmarkParams.chart_type === ChartType.Chart) {
      const sorting = convertOrDefaultFunnelStepsSortConfig(bookmarkParams);

      updatedParams = {
        ...updatedParams,
        sortField: undefined,
        sortOrder: undefined,
        sorting,
      };
    }
  }

  if (bookmarkParams.group_by?.length > 0) {
    if (bookmarkParams.savedLegend) {
      const updatedSavedLegend = bookmarkParams.savedLegend.map((section) => ({
        selected: section.selected.filter(({key}) => !isDeprecatedSegment(key)),
      }));
      if (!isEqual(updatedSavedLegend, bookmarkParams.savedLegend)) {
        updatedParams.savedLegend = updatedSavedLegend;
      }
    }
    if (bookmarkParams.selectedSegments) {
      const updatedSelectedSegments = bookmarkParams.selectedSegments.filter(({key}) => !isDeprecatedSegment(key));
      if (!isEqual(updatedSelectedSegments, bookmarkParams.selectedSegments)) {
        updatedParams.selectedSegments = updatedSelectedSegments;
      }
    }
  }

  const selectedSegments = updatedParams.selectedSegments ?? bookmarkParams.selectedSegments;
  if (!selectedSegments?.length && bookmarkParams.chart_type === ChartType.Trends && !bookmarkParams.group_by?.length) {
    // Old Funnel Trends bookmarks didn't persist `selectedSegments` for unsegmented queries. By modifying the bookmark
    // to include $overall, we prevent bookmark change detection issues with relying on SelectMode.Position to add
    // $overall to the `selectedSegments`
    updatedParams = {
      ...updatedParams,
      savedLegend: [{selected: [{key: OVERALL_PROP}]}],
      selectedSegments: [{color: getChartSegmentColorCombo(0).primary, key: OVERALL_PROP}],
    };
  }

  return updatedParams;
}

export function getSegmentDisplayName(segment: Segment): string {
  if (isCohortSegment(segment)) {
    const cohortSegment = segment[0];
    return propertiesUtils.renameProperty(cohortSegment[ResourceType.Cohort].name, ResourceType.Cohort);
  }
  const firstSegment = getFirstSegment(segment);
  if (isEventSegment(segment)) {
    return propertiesUtils.renameProperty(firstSegment.propertyName, ResourceType.Event, {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    });
  } else if (isUserSegment(segment)) {
    return propertiesUtils.renameProperty(firstSegment.propertyName, ResourceType.User, {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    });
  } else {
    const otherSegment = firstSegment as OtherSegmentation;
    return propertiesUtils.renameProperty(otherSegment.label ?? otherSegment.propertyName, ResourceType.Other, {
      dataGroupId: firstSegment.dataGroupId,
      customProperty: firstSegment.customProperty,
      propertyObjectKey: firstSegment.propertyObjectKey,
      listItemGroup: firstSegment.listItemGroup,
    });
  }
}

export function getSegmentColumnSpecs(bookmark: FunnelBookmark): SegmentColumnSpec[] {
  let segmentColumns: SegmentColumnSpec[] = [];

  bookmark.steps.forEach((el, idx) => {
    if (isOrStep(el)) {
      segmentColumns.push({
        title: `Step ` + (idx + 1) + ` Comparison`,
        segmentType: PropertyType.String,
      });
    }
  });

  if (bookmark.group_by) {
    segmentColumns = segmentColumns.concat(
      bookmark.group_by.map((segment) => {
        return {
          title: getSegmentDisplayName(segment),
          segmentType: getSegmentPropertyType(segment),
        };
      }),
    );
  }

  return segmentColumns;
}

export function isAnyOrderFunnel(funnelOrder: FunnelOrder) {
  return funnelOrder === FunnelOrder.Any;
}

export function hasDataTable(widgetType: WidgetType) {
  return [
    WidgetType.StepsMetricChart,
    WidgetType.StepChart,
    WidgetType.TTCMetricChart,
    WidgetType.TrendsChart,
  ].includes(widgetType);
}

export const widgetTypeToTrackName = {
  [WidgetType.StepsMetricChart]: DataTableTrackName.FunnelsStepsMetric,
  [WidgetType.StepChart]: DataTableTrackName.FunnelsSteps,
  [WidgetType.TTCMetricChart]: DataTableTrackName.FunnelsMedianMetric,
  [WidgetType.TrendsChart]: DataTableTrackName.FunnelsTrends,
};

export function getQuerySamplingFactor(results: FunnelQueryResponse): number {
  if (!results) {
    return 1;
  }

  return `metadata` in results ? results.metadata.min_sampling_factor : results.meta.min_sampling_factor;
}

export function addConversionRateToFunnelStep(funnelStep: FunnelStep): void {
  if (funnelStep.dropoff_step_freq_buckets && funnelStep.convert_step_freq_buckets) {
    funnelStep.convert_rate_freq_buckets = {
      buckets: funnelStep.dropoff_step_freq_buckets.buckets.map((_, i) => {
        return calculateAverage(
          funnelStep.convert_step_freq_buckets.buckets[i],
          funnelStep.dropoff_step_freq_buckets.buckets[i],
        );
      }),
      lower: calculateAverage(funnelStep.convert_step_freq_buckets.lower, funnelStep.dropoff_step_freq_buckets.lower),
      higher: calculateAverage(
        funnelStep.convert_step_freq_buckets.higher,
        funnelStep.dropoff_step_freq_buckets.higher,
      ),
    };

    funnelStep.convert_rate_freq_percentiles = funnelStep.dropoff_step_freq_percentiles?.map((_, i) => {
      return round(
        calculateAverage(funnelStep.convert_step_freq_percentiles[i], funnelStep.dropoff_step_freq_percentiles[i]),
        2,
      );
    });
  }
}

/**
 * Calculates the convert_rate_freq_buckets and convert_rate_freq_percentiles for Funnels frequency chart
 * @param {Array<DateKeyedFunnelSegment>} segmentData - the segment that needs convertion rate properties added to it
 * @return {Array<DateKeyedFunnelSegment>} - containing convert_rate_freq_buckets and convert_rate_freq_percentiles
 */
export function calculateFunnelFrequencyConversionRate(segmentData: DateKeyedFunnelSegments[]) {
  segmentData.forEach((dateKeyFunnelSegment) => {
    dateKeyFunnelSegment.segments.forEach((funnelSegment) => {
      funnelSegment.steps.forEach((step) => {
        const funnelStep: FunnelStep = step; // have to set type because it can be a BasicFunnelStep or FunnelStep

        addConversionRateToFunnelStep(funnelStep);
      });
    });
  });

  return segmentData;
}

function calculateAverage(conversion: number, dropOff: number) {
  return conversion / (dropOff + conversion) || 0;
}

export function createFunnelDataWithRename(
  funnelData: KeyedByDate<UnsegmentedFunnelResult | SegmentedFunnelResults>,
  funnelsQuery: FunnelsQuery,
): DateKeyedFunnelSegments[] {
  const segment = Object.values(funnelData)[0];
  let segmentRenameFunc = null;
  if (isSegmentedData(segment)) {
    const segmentNames = funnelsQuery.getSegmentNames();
    const segmentTypes = funnelsQuery.getSegmentTypes();
    segmentRenameFunc = (seg) => renameSegments(seg, segmentNames, segmentTypes);
  }
  return createFunnelData(funnelData, funnelsQuery.chartType, segmentRenameFunc);
}

/**
 * Given funnelData, return DateKeyedFunnelSegments that are optionally renamed and ordered by date
 * @param funnelData Array of result data mapped by Date
 * @param renameSegmentFunc Fuction to rename segments
 */
export function createFunnelData(
  funnelData: KeyedByDate<UnsegmentedFunnelResult | SegmentedFunnelResults>,
  chartType: ChartType,
  renameSegmentFunc: (FunnelSegment) => FunnelSegment = null,
): DateKeyedFunnelSegments[] {
  let dateKeyedSegments: DateKeyedFunnelSegments[] = Object.keys(funnelData).map((date) => {
    let segments: FunnelSegment[];
    const segment = funnelData[date];
    // If it is unsegmented, overall won't be keyed so we need to create the object
    if (isSegmentedData(segment)) {
      segments = collectNestedSegments(segment);
      /** We don't currently rename on dashboards so we need to pass in a rename func on Funnels */
      if (renameSegmentFunc) {
        segments = segments.map((seg) => renameSegmentFunc(seg));
      }
      segments = segments.filter((segment) => {
        // When using comparison events, they will be at the beginning of `segment.props`. Hence, we check the last
        // entry in `segment.props` for `$other`/`$overall`.
        const lastProp = segment.props[segment.props.length - 1].prop;
        return !DEPRECATED_SEGMENTS.includes(lastProp);
      });
    } else {
      segments = [generateOverallSegment(segment.steps)];
    }
    return {
      date: new Date(formatDateString(date)),
      segments,
    };
  });
  if (chartType === ChartType.Frequency) {
    dateKeyedSegments = calculateFunnelFrequencyConversionRate(dateKeyedSegments);
  }
  const allProps = getSegmentProps(dateKeyedSegments);
  dateKeyedSegments = dateKeyedSegments.map((dateKeyedSegment) => ({
    ...dateKeyedSegment,
    segments: addEmptySegments(dateKeyedSegment.segments, allProps),
  }));
  dateKeyedSegments.sort((a, b) => a.date.getTime() - b.date.getTime());
  return dateKeyedSegments;
}

/**
 * Order Funnel Segments by highest to lowest absolute
 * number of average funnel conversions during the time period.
 * Also ensures that every date key's value has the same number of segments.
 */
export function orderSegmentsbyTotalConversions(funnelSegments: DateKeyedFunnelSegments[]): DateKeyedFunnelSegments[] {
  if (!funnelSegments || funnelSegments.length === 0) {
    return [];
  }
  const allProps = getSegmentProps(funnelSegments);
  const totalConversionsMap = getSegmentsToTotalConversionsMap(Object.keys(allProps), funnelSegments);
  return funnelSegments.map(({date, segments}) => {
    const orderedSegments = addEmptySegments(segments, allProps) //ensures all props have steps for all date values
      .sort((a, b) => {
        if (a.prop === OVERALL_PROP) {
          return -1;
        } else if (b.prop === OVERALL_PROP) {
          return 1;
        } else {
          return totalConversionsMap.get(b.prop) - totalConversionsMap.get(a.prop);
        }
      });
    return {date, segments: orderedSegments};
  });
}

/**
 * Given the segmented over a certain time range,
 * returns a SegmentPropToDetailsMap containing the complete set of props across time buckets.
 */
function getSegmentProps(funnelSegments: DateKeyedFunnelSegments[]): SegmentPropToDetailsMap {
  const propToDetailsMap = {};
  funnelSegments.forEach(({segments}) => {
    segments.forEach(({prop, propName, props, steps}) => {
      propToDetailsMap[prop] = {propName, props, steps};
    });
  });
  return propToDetailsMap;
}

/**
 * Given segmented data over a date range, returns a map of segment labels
 * to the sum of all numerical funnel conversions over that date range
 * @return {Map<string, number>} map of segment label to sum of funnel conversions
 */
function getSegmentsToTotalConversionsMap(
  allProps: string[],
  funnelSegments: DateKeyedFunnelSegments[],
): Map<string, number> {
  const segmentMap = new Map();
  allProps.forEach((prop) => {
    segmentMap.set(prop, 0);
  });
  funnelSegments.forEach(({segments}) => {
    segments.forEach(({prop, steps}) => {
      const currentVal = segmentMap.get(prop);
      segmentMap.set(prop, currentVal + steps[steps.length - 1].count);
    });
  });
  return segmentMap;
}

/**
 * Given an array of segments and a set of props "allProps" (a
 * superset of all the segment props),
 * returns an augmented array of segments with an entry for every
 * prop in "allProps", with potentially empty steps.
 */
function addEmptySegments(segments: FunnelSegment[], allProps: SegmentPropToDetailsMap): FunnelSegment[] {
  // Conflicted on using OVERALL_PROP here or using assumption that there will be segments that contain steps
  const nonNullProps = segments.map(({prop}) => prop);
  const nullProps = difference(Object.keys(allProps), nonNullProps);
  // adds funnels segments with empty steps to ensure every prop is in the final segments object
  const emptySegments = nullProps.map((prop) => {
    const {propName, props, steps} = allProps[prop];
    const emptyStepData = createBlankSteps(steps);
    return {prop, propName, props, steps: emptyStepData};
  });
  return [...segments, ...emptySegments];
}

/**
 * Given an array of date keyed segments and an array of selected segments,
 * returns an array with only the date keyed segmented whose prop values are
 * in the provided selected segments array.
 *
 * @param funnelSegments array of date keyed funnel segments
 * @param selectedSegments array of the selected segments (selected segments are represented as key to color objects)
 * @return {DateKeyedFunnelSegments[]} array of the funnel segments whose prop values are present provided selected segments array
 */
export function filterForSelectedSegments(
  funnelSegments: DateKeyedFunnelSegments[],
  selectedSegments: ColorChoice[],
): DateKeyedFunnelSegments[] {
  const selectedFunnelSegments: DateKeyedFunnelSegments[] = [];
  const selectedProps = selectedSegments.map((s) => s.key);
  funnelSegments.forEach(({date, segments}) => {
    selectedFunnelSegments.push({
      date,
      segments: segments.filter((segment) => {
        return selectedProps.includes(segment.prop);
      }),
    });
  });
  return selectedFunnelSegments;
}

/**
 * Use FunnelSegment to get step info to create an empty Array of FunnelSteps
 */
export function createBlankSteps(steps: FunnelSegment['steps']): Array<BasicFunnelStep | FunnelStep> {
  return steps.map((step) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {custom_event, custom_event_id, event, goal, index, step_label} = step;
    const blankPValue = {insufficient_samples: true, p: 0, confidence: 0, is_interesting: false};
    return {
      avg_time: null,
      avg_time_from_start: null,
      count: 0,
      overall_conv_ratio: 0,
      step_conv_ratio: 0,
      p_value: blankPValue,
      custom_event,
      custom_event_id,
      event,
      goal,
      index,
      step_label,
    };
  });
}

/**
 * When comparing events and segmenting, $overall is present once per compared segment.
 * In such cases, we need to handle renaming on each individual segment, depth level.
 */
function renameIfOverall(propName) {
  if (propName === OVERALL_PROP) {
    return OVERALL_PROP_NAME;
  }
  return propName;
}

/**
 * Rename all segment values. Returns a FunnelSegment with renamed
 * segment.propName, which is used for display, and renamed segment.props' propName.
 */
export function renameSegments(
  segment: FunnelSegment,
  segmentNames: Array<string | string[]>,
  segmentTypes: string[],
): FunnelSegment {
  const returnedSegment = {...segment};
  if (returnedSegment.prop === OVERALL_PROP) {
    returnedSegment.propName = OVERALL_PROP_NAME;
  } else if (returnedSegment.props.length !== segmentNames.length) {
    // uneven lengths indicate a special property like $other which isn't renamed,
    // so just set propName to the props
    returnedSegment.propName = returnedSegment.props.map((seg) => renameIfOverall(seg.prop)).join(SEGMENT_SEPARATOR);
  } else {
    returnedSegment.props = zip(returnedSegment.props, segmentNames, segmentTypes).map((value) => {
      const [segmentId, name, type] = value;
      const propName = renameSegmentProperty({
        segmentValue: segmentId.prop,
        segmentName: name,
        segmentType: type,
      });
      return {prop: segmentId.prop, propName};
    });
    returnedSegment.propName = returnedSegment.props
      .map((seg) => renameIfOverall(seg.propName))
      .join(SEGMENT_SEPARATOR);
  }
  if (returnedSegment.propName === ``) {
    returnedSegment.propName = ` `;
  }
  return returnedSegment;
}

/**
 * Renames the segment property to a more friendly display name
 * based on the type of segmentation. Returns the renamed property.
 */
export function renameSegmentProperty({
  segmentValue,
  segmentName,
  segmentType,
}: {
  segmentValue: string;
  segmentName: string | string[];
  segmentType: string;
}): string {
  if (!segmentValue) {
    return ``;
  } else if (segmentType === PropertyType.String) {
    return renamePropertyValue(segmentValue, segmentName);
  }
  return segmentValue;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getPropertyFilterLabels(step) {
  const MAX_FILTERS_FOR_DISPLAY = 3;
  const selectorParams = step.selector_params;
  if (selectorParams) {
    const operator = selectorParams.bool_op;
    let propertyFilters = selectorParams.property_filter_params_list;
    if (propertyFilters) {
      const remainingFilters = propertyFilters.length - MAX_FILTERS_FOR_DISPLAY;
      propertyFilters = propertyFilters.slice(0, MAX_FILTERS_FOR_DISPLAY);
      const transformedPropertyFilters = propertyFilters.map((filter) => {
        if (isSegmentationFilter(filter)) {
          if (filter instanceof PropertyFilter) {
            return filter.getLabel();
          }

          return new PropertyFilter(filter).getLabel();
        } else if (filter.property.name === `$experiments`) {
          // Property filters of type 'object' are not currently supported
          // Since this is just using the property filter instance for generating
          // a label, we'll manually create the label we want for now
          // a variant is selected
          if (filter.filter.operand) {
            return renamePropertyValue(filter.filter.operand, `$variant_id`);
          } else {
            return `Experiment is ${renamePropertyValue(filter.filter.segments[1], `$experiment_id`)}`;
          }
        } else {
          return convertSegfilterToPropertyFilter(filter).getLabel();
        }
      });
      let filterLabels = transformedPropertyFilters.join(` ${operator} `);
      if (remainingFilters > 0) {
        filterLabels = `${filterLabels} and ${remainingFilters} other ${pluralize(`condition`, remainingFilters)}`;
      }
      return {
        propertyFilterText: filterLabels,
      };
    }
  }
  return {
    propertyFilters: [],
    propertyFilterText: null,
  };
}

export function getSegments(segmentData: DateKeyedFunnelSegments[]) {
  return segmentData?.[0]?.segments;
}
